import {isFeatureEnabled} from '@github-ui/feature-flags'
import type {IssueFieldSelection, IssueFieldSelectionValues} from '@github-ui/issue-fields-shared/issue-field'
import {
  asIssueFieldSelection,
  getIssueFieldValue,
  parsePinnedIssueFields,
  toIssueFieldType,
} from '@github-ui/issue-fields-shared/issue-field'
import {useIssueFieldsAnalytics} from '@github-ui/issue-fields-shared/use-issue-fields-analytics'
import {noop} from '@github-ui/noop'
import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {Heading, Link, Tooltip} from '@primer/react'
import {useCallback, useEffect, useMemo, useState} from 'react'
import {useFragment, useRelayEnvironment} from 'react-relay'
import {graphql} from 'relay-runtime'

import {
  areFieldValuesEqual,
  createClearedFieldValue,
  getFieldChanges,
  getIssueFieldSelectionValue,
  type IssueFieldWithUnsavedValue,
  toIssueFieldWithUnsavedValue,
} from '../../utils/issue-field-helper'
import {Section} from '../Section'
import type {FieldsSectionFieldValues$key, IssueFieldDataType} from './__generated__/FieldsSectionFieldValues.graphql'
import type {FieldsSectionFragment$key} from './__generated__/FieldsSectionFragment.graphql'
import {AddIssueFieldButton} from './AddIssueFieldButton'
import {LABELS} from './constants/labels'
import {FieldsSelector} from './FieldSelector'
import styles from './FieldsSection.module.css'
import {
  getPinnedFieldClassName,
  getPinnedFieldPosition,
  runFieldMutation,
  usePendingFieldFocus,
  usePinnedFieldsAnimation,
} from './helpers'
import {ErrorDialog, getIssueFieldEditor, getPendingEditor, type SafeFieldValue} from './IssueFieldEditors'
import {commitCreateIssueFieldValueMutation} from './mutations/create-issue-field-value-mutation'
import {commitDeleteIssueFieldValueMutation} from './mutations/delete-issue-field-value-mutation'
import {commitUpdateIssueFieldValueMutation} from './mutations/update-issue-field-value-mutation'

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
export const fieldsSectionFragment = graphql`
  fragment FieldsSectionFragment on Issue {
    id
    repository {
      isPrivate
      owner {
        login
        databaseId
        ... on Organization {
          issueFieldsEnabled: isFeatureEnabled(name: "issue_fields")
          issueFieldsVisibilitySettingsEnabled: isFeatureEnabled(name: "issue_fields_visibility_settings")
          pinnedIssueFields(first: 100) {
            nodes {
              __typename
              ... on IssueFieldText {
                id
                name
                dataType
                visibility
              }
              ... on IssueFieldSingleSelect {
                id
                name
                dataType
                visibility
              }
              ... on IssueFieldDate {
                id
                name
                dataType
                visibility
              }
              ... on IssueFieldNumber {
                id
                name
                dataType
                visibility
              }
            }
          }
        }
      }
    }
    viewerCanSetFields
    ...FieldsSectionFieldValues
    issueType {
      id
      name
      pinnedFields {
        __typename
        ... on IssueFieldText {
          id
          name
          dataType
          visibility
        }
        ... on IssueFieldSingleSelect {
          id
          name
          dataType
          visibility
        }
        ... on IssueFieldDate {
          id
          name
          dataType
          visibility
        }
        ... on IssueFieldNumber {
          id
          name
          dataType
          visibility
        }
      }
    }
  }
`

// eslint-disable-next-line @github-ui/github-monorepo/restrict-relay-queries
export const fieldsSectionFieldValuesFragment = graphql`
  fragment FieldsSectionFieldValues on Issue {
    id
    issueFieldValues(first: 25) {
      nodes {
        ... on IssueFieldTextValue {
          field {
            ... on IssueFieldText {
              id
              name
              dataType
              visibility
            }
          }
          value
        }
        ... on IssueFieldSingleSelectValue {
          field {
            ... on IssueFieldSingleSelect {
              id
              name
              dataType
              visibility
            }
          }
          # eslint-disable-next-line relay/unused-fields // this is used in parseIssueFieldValues
          optionId
          name
          # eslint-disable-next-line relay/unused-fields // this is used in IssueFieldEditors.tsx
          color
          # eslint-disable-next-line relay/unused-fields // this is used in IssueFieldEditors.tsx
          description
        }
        ... on IssueFieldDateValue {
          field {
            ... on IssueFieldDate {
              id
              name
              dataType
              visibility
            }
          }
          value
        }
        ... on IssueFieldNumberValue {
          field {
            ... on IssueFieldNumber {
              id
              name
              dataType
              visibility
            }
          }
          # eslint-disable-next-line relay/unused-fields
          numberValue: value
        }
      }
    }
    viewerCanSetFields
    issueType {
      id
      name
    }
  }
`

export type EditIssueFieldsSectionProps = {
  onUpdate: () => void
  issue: FieldsSectionFragment$key
  inProjectsPage?: boolean
  readonly: boolean
}

export function EditIssueFieldsSection({issue, onUpdate, inProjectsPage, readonly}: EditIssueFieldsSectionProps) {
  const [fieldsSet, setFieldsSet] = useState<string[]>([])
  const [mutationErrorPresent, setMutationErrorPresent] = useState<boolean>(false)
  const [errorDialogOpen, setErrorDialogOpen] = useState<boolean>(false)
  const [pendingField, setPendingField] = useState<IssueFieldSelection | null>(null)
  const environment = useRelayEnvironment()
  const {markInteractionStart, reportUsage} = useIssueFieldsAnalytics()
  const {setAddFieldButtonElement} = usePendingFieldFocus(pendingField)

  const hideAddFieldButton = !isFeatureEnabled('issue_metadata_add_field_button')

  const data = useFragment(fieldsSectionFragment, issue)

  const {
    repository: {
      isPrivate,
      owner: {login: owner},
    },
    viewerCanSetFields,
    issueType,
  } = data

  const issueFieldsEnabled = data.repository.owner.issueFieldsEnabled ?? false

  const orgPinnedFields: readonly IssueFieldSelection[] | null = useMemo(() => {
    return parsePinnedIssueFields(
      data.repository.owner.pinnedIssueFields?.nodes?.filter(node => node?.__typename !== '%other') ?? [],
    )
  }, [data.repository.owner.pinnedIssueFields])

  // Use issue type pinned fields if a type is selected, otherwise org pinned fields
  const mergedPinnedFields: readonly IssueFieldSelection[] = useMemo(
    () =>
      issueType
        ? (parsePinnedIssueFields(issueType.pinnedFields?.filter(f => f?.__typename !== '%other')) ?? [])
        : (orgPinnedFields ?? []),
    [issueType, orgPinnedFields],
  )

  const pinnedFields = useMemo(() => {
    return mergedPinnedFields.map(field => toIssueFieldWithUnsavedValue(asIssueFieldSelection(field), null))
  }, [mergedPinnedFields])

  const handleErrors = useCallback(() => {
    setMutationErrorPresent(true)
  }, [])

  const onCommitPendingField = useCallback(
    (fieldId: string, payload: string | null) => {
      if (!pendingField || pendingField.id !== fieldId) return

      // Remove from pending state
      setPendingField(null)

      const value = getIssueFieldSelectionValue(pendingField.dataType, payload || '')
      const isEmpty = typeof value === 'string' ? !value.trim() : !value

      // Remove from fieldsSet if field is being cancelled/removed
      if (pendingField.name && isEmpty) {
        setFieldsSet(prev => prev.filter(name => name !== pendingField.name))
        return
      }

      // If value is empty, don't make API call (field is being removed)
      if (isEmpty) {
        return
      }

      // Make API call to create the field with the committed value
      runFieldMutation({
        mutationFn: commitCreateIssueFieldValueMutation,
        input: {
          issueId: data.id,
          issueField: {
            fieldId,
            ...getIssueFieldValue(pendingField.dataType, value),
          },
        },
        environment,
        action: 'create',
        fieldId,
        fieldType: toIssueFieldType(pendingField.dataType),
        inProjectsPage,
        ownerId: data.repository?.owner?.databaseId,
        reportUsage,
        onError: handleErrors,
      })
    },
    [pendingField, data.id, data.repository.owner.databaseId, environment, inProjectsPage, reportUsage, handleErrors],
  )

  const onIssueFieldSelected = (selectedField: IssueFieldSelection) => {
    if (!selectedField.id) return
    setPendingField(selectedField)

    // Add to fieldsSet to prevent it from showing in selector again
    if (selectedField.name) {
      setFieldsSet(prev => [...prev, selectedField.name])
    }
  }

  const visibilityEnabled = data.repository.owner.issueFieldsVisibilitySettingsEnabled ?? false
  const showVisibilityIndicators =
    !isPrivate && visibilityEnabled && !!viewerCanSetFields && isFeatureEnabled('issue_fields_visibility_indicator')
  if ((!isPrivate && !visibilityEnabled) || !issueFieldsEnabled) {
    return null
  }

  return (
    <>
      <ErrorBoundary
        fallback={
          <div>
            <Tooltip text={LABELS.loadingFieldsTooltipMessage}>
              <span>
                <AddIssueFieldButton inactive anchorProps={{onClick: () => setErrorDialogOpen(true)}} onClick={noop} />
              </span>
            </Tooltip>
            {errorDialogOpen && (
              <ErrorDialog onClose={() => setErrorDialogOpen(false)} message={LABELS.loadingFieldsErrorMessage} />
            )}
          </div>
        }
      >
        <IssueFields
          issue={data}
          fieldsSet={fieldsSet}
          setFieldsSet={setFieldsSet}
          inProjectsPage={inProjectsPage}
          pinnedFields={pinnedFields}
          readonly={readonly}
          onUpdate={onUpdate}
          showVisibilityIndicators={showVisibilityIndicators}
        />

        {/* Render pending field */}
        {pendingField &&
          (() => {
            const isPinned = pinnedFields.some(
              (field: IssueFieldWithUnsavedValue) => field.field.id === pendingField.id,
            )
            return getPendingEditor({
              fieldSelection: pendingField,
              readonly: false,
              onCommit: onCommitPendingField,
              className: isPinned ? styles.pinnedField : undefined,
            })
          })()}
        {!readonly && viewerCanSetFields && (
          <FieldsSelector
            owner={owner}
            fieldsSet={fieldsSet}
            onIssueFieldSelected={onIssueFieldSelected}
            markInteractionStart={markInteractionStart}
            disabled={pendingField !== null}
            onButtonElementReceived={setAddFieldButtonElement}
            hideAddFieldButton={hideAddFieldButton}
          />
        )}
      </ErrorBoundary>

      {mutationErrorPresent && (
        <ErrorDialog onClose={() => setMutationErrorPresent(false)} message={LABELS.mutationErrorMessage} />
      )}
    </>
  )
}

function IssueFields({
  issue,
  fieldsSet,
  setFieldsSet,
  inProjectsPage,
  onUpdate,
  pinnedFields,
  readonly,
  showVisibilityIndicators,
}: {
  issue: FieldsSectionFieldValues$key
  fieldsSet: string[]
  setFieldsSet: React.Dispatch<React.SetStateAction<string[]>>
  inProjectsPage?: boolean
  pinnedFields?: IssueFieldWithUnsavedValue[]
  readonly?: boolean
  onUpdate: () => void
  showVisibilityIndicators?: boolean
}) {
  const environment = useRelayEnvironment()
  const [errorPresent, setErrorPresent] = useState<Record<string, boolean>>({})
  const {reportUsage} = useIssueFieldsAnalytics()

  // Add beforeunload event handler for field-level errors
  useEffect(() => {
    const hasAnyErrors = Object.values(errorPresent).some(isError => isError)

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (hasAnyErrors) {
        event.preventDefault()
        // This message will be displayed by browsers when users try to reload the page
        event.returnValue = LABELS.reloadWarningMessage
        return event.returnValue
      }
    }

    window.addEventListener('beforeunload', handleBeforeUnload)
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload)
    }
  }, [errorPresent])
  const data = useFragment(fieldsSectionFieldValuesFragment, issue)

  // Use issue type ID for animation instead of pinned field IDs
  // This ensures ALL pinned fields animate when issue type changes, not just newly added ones
  const issueTypeId = data.issueType?.id || null
  const {animatePinned} = usePinnedFieldsAnimation(issueTypeId)

  const filteredFieldValues: SafeFieldValue[] = (data.issueFieldValues?.nodes || []).map(fv => fv as SafeFieldValue)

  const [effectiveFieldValues, setEffectiveFieldValues] = useState<SafeFieldValue[]>(
    filteredFieldValues.filter(fv => fv?.field != null),
  )

  // Reset effectiveFieldValues when navigating to a different issue
  useEffect(() => {
    const newFilteredFieldValues: SafeFieldValue[] = (data.issueFieldValues?.nodes || []).map(
      fv => fv as SafeFieldValue,
    )
    setEffectiveFieldValues(newFilteredFieldValues.filter(fv => fv?.field != null))
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Only reset when issue ID changes, not on every field update
  }, [data.id])

  const handleErrors = useCallback((fieldId: string) => {
    setErrorPresent(prev => ({...prev, [fieldId]: true}))
  }, [])

  const onCommitField = useCallback(
    (fieldId: string, fieldType: IssueFieldDataType, value: IssueFieldSelectionValues) => {
      const issueFieldType = toIssueFieldType(fieldType)
      const selectionValue = getIssueFieldSelectionValue(issueFieldType, value)
      if (value === '' || value === null) {
        runFieldMutation({
          mutationFn: commitDeleteIssueFieldValueMutation,
          input: {
            issueId: data.id,
            fieldId,
          },
          environment,
          action: 'delete',
          fieldId,
          fieldType: issueFieldType,
          inProjectsPage,
          reportUsage,
          onCompleted: () => {
            setErrorPresent(prev => ({...prev, [fieldId]: false}))
            onUpdate()
          },
          onError: () => handleErrors(fieldId),
        })
      } else {
        runFieldMutation({
          mutationFn: commitUpdateIssueFieldValueMutation,
          input: {
            issueId: data.id,
            issueField: {
              fieldId,
              ...getIssueFieldValue(issueFieldType, selectionValue),
            },
          },
          environment,
          action: 'update',
          fieldId,
          fieldType: issueFieldType,
          inProjectsPage,
          reportUsage,
          onCompleted: () => {
            setErrorPresent(prev => ({...prev, [fieldId]: false}))
            onUpdate()
          },
          onError: () => handleErrors(fieldId),
        })
      }
    },
    [data.id, environment, handleErrors, inProjectsPage, reportUsage, onUpdate],
  )

  useMemo(() => {
    const {addedFields, updatedFields, removedFields} = getFieldChanges(effectiveFieldValues, filteredFieldValues)

    if (addedFields.length === 0 && updatedFields.length === 0 && removedFields.length === 0) {
      return
    }

    let shouldSet = addedFields.length > 0

    // We want to maintain the initial order of fields, so we iterate over effectiveFieldValues
    const updatedFieldValues = effectiveFieldValues
      .map(fieldValue => {
        const updatedField = updatedFields.find(uf => uf?.field?.id === fieldValue?.field?.id)
        if (updatedField) {
          if (!areFieldValuesEqual(updatedField, fieldValue)) shouldSet = true
          return updatedField
        }

        // Handle field removals: clear value for all fields
        const isRemoved = removedFields.some(
          (removedField: (typeof filteredFieldValues)[number]) => removedField?.field?.id === fieldValue?.field?.id,
        )
        if (isRemoved) {
          const clearedField = createClearedFieldValue(fieldValue)
          if (!areFieldValuesEqual(clearedField, fieldValue)) shouldSet = true
          return clearedField
        }

        return fieldValue
      })
      .filter(fv => fv !== null && fv !== undefined)

    if (shouldSet) setEffectiveFieldValues([...updatedFieldValues, ...addedFields])
  }, [filteredFieldValues, effectiveFieldValues])

  // Merge pinned fields at the beginning, then existing fields
  const allFields = useMemo(() => {
    if (!pinnedFields || pinnedFields.length === 0) {
      return effectiveFieldValues
    }

    // If any of the pinned fields already exists as a user-set field with a value, we use it and its existing value, otherwise we use the default pinned field
    const newPinnedFields = pinnedFields.map(pinnedField => {
      const userSetField = effectiveFieldValues.find(fieldValue => fieldValue.field.id === pinnedField.field.id)
      return userSetField ? userSetField : pinnedField
    })

    // remove the user-set fields that are now pinned and move unpinned fields with values to the user-set list
    const pinnedIds = newPinnedFields.map(pinnedField => pinnedField.field.id)
    const userSetFields = effectiveFieldValues.filter(fv => !pinnedIds.includes(fv.field.id))

    return [...newPinnedFields, ...userSetFields]
  }, [pinnedFields, effectiveFieldValues])

  // Update fieldsSet to include all visible fields (both with values and cleared fields)
  // to prevent them from appearing in the dropdown picker
  useEffect(() => {
    const allVisibleFieldNames = allFields.map(fv => fv?.field?.name || '').filter(name => name)
    const arraysAreEqual =
      fieldsSet.length === allVisibleFieldNames.length &&
      fieldsSet.every((name, idx) => name === allVisibleFieldNames[idx])
    if (!arraysAreEqual) {
      setFieldsSet(allVisibleFieldNames)
    }
  }, [fieldsSet, allFields, setFieldsSet])

  // Get all field IDs in order for position calculation
  const allFieldIds = allFields.map(fv => fv?.field?.id).filter((id): id is string => !!id)
  // Build a Set of pinned field IDs for position calculation
  const pinnedFieldIdSet = new Set(pinnedFields?.map(pf => pf.field.id).filter((id): id is string => !!id) ?? [])

  const fieldValues = allFields.map(fieldValue => {
    if (!fieldValue || !fieldValue.field || !fieldValue.field.id) return null
    if (fieldValue.field === undefined || fieldValue.field.dataType === undefined) return null

    // Check if this field is pinned
    const isPinned = pinnedFields?.some(pf => pf.field.id === fieldValue.field.id) || false
    const readonlyField = readonly || !data.viewerCanSetFields
    const position = getPinnedFieldPosition(fieldValue.field.id, pinnedFieldIdSet, allFieldIds)

    // Show visibility indicator for public fields
    const fieldVisibility = 'visibility' in fieldValue.field ? fieldValue.field.visibility : undefined
    const shouldShowVisibilityIndicator = showVisibilityIndicators && fieldVisibility === 'ALL'

    return getIssueFieldEditor({
      errorPresent,
      setErrorPresent: (fieldId: string, present: boolean) => setErrorPresent(prev => ({...prev, [fieldId]: present})),
      // We need the casting because the field.id property can be undefined in GraphQL
      fieldValue: fieldValue as SafeFieldValue,
      readonly: readonlyField,
      onCommit: (fieldId: string, value: IssueFieldSelectionValues) =>
        onCommitField(fieldId, fieldValue.field.dataType!, value),
      className: getPinnedFieldClassName(isPinned, animatePinned, position),
      showVisibilityIndicator: shouldShowVisibilityIndicator,
    })
  })

  const visibleFieldValues = fieldValues.filter(Boolean)

  return (
    <Section
      sectionHeader={
        <div className={styles.sectionHeaderWrapper}>
          <Heading as="h3" className={styles.compactSectionHeader}>
            Fields
          </Heading>
          <Link
            href="https://github.com/orgs/community/discussions/189141"
            target="_blank"
            rel="noopener noreferrer"
            className={styles.giveFeedbackLink}
          >
            Give feedback
          </Link>
        </div>
      }
    >
      {visibleFieldValues.length > 0 ? (
        <>{visibleFieldValues}</>
      ) : (
        <span className={styles.compactSectionEmptyText}>{LABELS.noPinnedFieldsMessage(data.issueType?.name)}</span>
      )}
    </Section>
  )
}
